// ProposalsHistorySidebar.tsx
// Right-hand slide-in panel listing every proposal sent to the lead. The card shows
// only the latest few; the rows (same as on the card) are passed in as children, so
// accept / reject / reopen / resend / view all work from here too.

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FileText, X } from 'lucide-react';
import { G, C } from '../../../components/GlobalComponents/uiTokens';
import { INNER } from '../LeadDetails';

interface ProposalsHistorySidebarProps {
  visible: boolean;
  onHide: () => void;
  count: number;
  leadName: string;
  children: React.ReactNode;
}

const ProposalsHistorySidebar: React.FC<ProposalsHistorySidebarProps> = ({ visible, onHide, count, leadName, children }) => {
  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onHide();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, onHide]);

  if (!visible) return null;

  return createPortal(
    <>
      <style>{`@keyframes ldv-ph-slide-in { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>

      <div onClick={onHide} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,22,0.35)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)', zIndex: 1000 }} />

      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          width: 'min(520px, 100vw)',
          background: G.shellBg,
          borderLeft: `1px solid ${G.shellBorder}`,
          boxShadow: '-8px 0 30px rgba(17,85,80,0.18)',
          zIndex: 1001,
          display: 'flex',
          flexDirection: 'column',
          animation: 'ldv-ph-slide-in 0.22s ease-out',
        }}
      >
        <div style={{ ...INNER, borderRadius: 0, borderTop: 'none', borderRight: 'none', borderLeft: 'none', padding: '13.8px 16.6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9.2px', minWidth: 0 }}>
            <div style={{ width: '35px', height: '35px', borderRadius: '10px', background: C.lime, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.teal, flexShrink: 0 }}>
              <FileText size={16} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '0.93rem', fontWeight: 700, color: C.teal }}>All proposals</div>
              <span style={{ fontSize: '0.68rem', color: C.muted }}>
                {count} sent to {leadName}
              </span>
            </div>
          </div>
          <button onClick={onHide} aria-label="Close" style={{ width: '30px', height: '30px', borderRadius: '8px', border: `1px solid ${G.line}`, background: 'transparent', color: C.muted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <X size={15} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '13.8px', display: 'flex', flexDirection: 'column', gap: '7px' }}>{children}</div>
      </div>
    </>,
    document.body,
  );
};

export default ProposalsHistorySidebar;
